import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  calculateOrderTotal,
  createOrder,
} from "../../../application/actions/orderActions";
import { fetchActivePackages } from "../../../application/actions/packageActions";
import { searchProducts } from "../../../application/actions/supplierActions";
import {
  addToCart,
  clearCart,
  removeFromCart,
  updateCartItemQuantity,
} from "../../../application/slices/orders";

const OrderCreate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Redux State
  const packages = useSelector((state) => state.packages.packages.data);
  const packagesLoading = useSelector(
    (state) => state.packages.packages.loading
  );
  const products = useSelector((state) => state.suppliers.searchResults.data);
  const productsLoading = useSelector(
    (state) => state.suppliers.searchResults.loading
  );
  const cart = useSelector((state) => state.orders.cart);
  const orderTotal = useSelector((state) => state.orders.orderTotal);

  // Local State
  const [activeStep, setActiveStep] = useState(0);
  const [orderType, setOrderType] = useState("package"); // 'package' or 'products'
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [deliveryInfo, setDeliveryInfo] = useState({
    address: "",
    city: "",
    postalCode: "",
    phoneNumber: "",
    deliveryDate: "",
    notes: "",
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const steps = [
    "انتخاب محصولات",
    "تجهیزات اضافی",
    "اطلاعات تحویل",
    "بررسی نهایی",
  ];

  // Check if package is preselected from location state
  useEffect(() => {
    if (location.state?.packageId) {
      setSelectedPackageId(location.state.packageId);
      setOrderType("package");
    }
  }, [location.state]);

  useEffect(() => {
    if (activeStep === 0) {
      dispatch(fetchActivePackages({ pageNumber: 1, pageSize: 100 }));
    }
  }, [activeStep, dispatch]);

  useEffect(() => {
    if (activeStep === 1) {
      dispatch(
        searchProducts({
          pageNumber: 1,
          pageSize: 50,
          searchTerm: productSearch,
          isAvailable: true,
        })
      );
    }
  }, [activeStep, dispatch, productSearch]);

  // Calculate total whenever cart changes
  useEffect(() => {
    if (cart.items && cart.items.length > 0) {
      const items = cart.items.map((item) => ({
        productId: item.productId,
        quantity: item.quantity,
        price: item.price,
      }));

      dispatch(
        calculateOrderTotal({
          packageId: selectedPackageId,
          items,
        })
      );
    }
  }, [cart, selectedPackageId, dispatch]);

  const handleNext = () => {
    if (activeStep === 0) {
      if (orderType === "package" && !selectedPackageId) {
        toast.error("لطفاً یک پکیج انتخاب کنید");
        return;
      }
      if (orderType === "products" && cart.items.length === 0) {
        toast.error("لطفاً حداقل یک محصول به سبد اضافه کنید");
        return;
      }
    }

    if (activeStep === 2) {
      if (
        !deliveryInfo.address ||
        !deliveryInfo.city ||
        !deliveryInfo.phoneNumber
      ) {
        toast.error("لطفاً تمام فیلدهای ضروری را پر کنید");
        return;
      }
    }

    if (activeStep === 3) {
      if (!acceptTerms) {
        toast.error("لطفاً شرایط و قوانین را تأیید کنید");
        return;
      }
      handleSubmitOrder();
      return;
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSelectPackage = (packageId) => {
    setSelectedPackageId(packageId);
    setOrderType("package");
  };

  const handleAddToCart = (product) => {
    dispatch(
      addToCart({
        productId: product.id,
        productName: product.name,
        price: product.price,
        quantity: 1,
      })
    );
    toast.success(`${product.name} به سبد خرید اضافه شد`);
  };

  const handleRemoveFromCart = (productId) => {
    dispatch(removeFromCart(productId));
    toast.info("محصول از سبد خرید حذف شد");
  };

  const handleUpdateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      handleRemoveFromCart(productId);
      return;
    }
    dispatch(updateCartItemQuantity({ productId, quantity: newQuantity }));
  };

  const handleDeliveryInfoChange = (field, value) => {
    setDeliveryInfo({
      ...deliveryInfo,
      [field]: value,
    });
  };

  const handleSubmitOrder = async () => {
    setSubmitting(true);

    try {
      const orderData = {
        packageId: orderType === "package" ? selectedPackageId : null,
        items: cart.items.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          price: item.price,
        })),
        deliveryAddress: deliveryInfo.address,
        city: deliveryInfo.city,
        postalCode: deliveryInfo.postalCode,
        phoneNumber: deliveryInfo.phoneNumber,
        deliveryDate: deliveryInfo.deliveryDate || null,
        notes: deliveryInfo.notes,
      };

      const result = await dispatch(createOrder(orderData)).unwrap();

      toast.success("سفارش با موفقیت ثبت شد");
      dispatch(clearCart());

      // Navigate to order details
      navigate(`/orders/${result.id}`);
    } catch (error) {
      toast.error(error.message || "خطا در ثبت سفارش");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedPackage = packages?.find((pkg) => pkg.id === selectedPackageId);

  const iranCities = [
    "تهران",
    "مشهد",
    "اصفهان",
    "شیراز",
    "تبریز",
    "کرج",
    "قم",
    "اهواز",
    "کرمان",
    "رشت",
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate("/orders")}
          sx={{ mb: 2 }}
        >
          بازگشت به سفارشات
        </Button>
        <Typography variant="h4" component="h1" gutterBottom>
          ثبت سفارش جدید
        </Typography>
        <Typography variant="body1" color="text.secondary">
          انتخاب پکیج یا محصولات و تکمیل اطلاعات تحویل
        </Typography>
      </Box>

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Step 0: Select Package or Products */}
      {activeStep === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            انتخاب نوع سفارش
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  border: orderType === "package" ? 2 : 1,
                  borderColor:
                    orderType === "package" ? "primary.main" : "divider",
                  cursor: "pointer",
                }}
                onClick={() => setOrderType("package")}
              >
                <CardContent>
                  <Typography variant="h6">سفارش پکیج</Typography>
                  <Typography variant="body2" color="text.secondary">
                    انتخاب یک پکیج کامل خورشیدی
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  border: orderType === "products" ? 2 : 1,
                  borderColor:
                    orderType === "products" ? "primary.main" : "divider",
                  cursor: "pointer",
                }}
                onClick={() => setOrderType("products")}
              >
                <CardContent>
                  <Typography variant="h6">سفارش محصولات</Typography>
                  <Typography variant="body2" color="text.secondary">
                    انتخاب محصولات دلخواه
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {orderType === "package" && (
            <>
              <Typography variant="h6" gutterBottom>
                انتخاب پکیج
              </Typography>

              {packagesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {packages?.map((pkg) => (
                    <Grid item xs={12} md={6} lg={4} key={pkg.id}>
                      <Card
                        sx={{
                          border: selectedPackageId === pkg.id ? 2 : 1,
                          borderColor:
                            selectedPackageId === pkg.id
                              ? "primary.main"
                              : "divider",
                          cursor: "pointer",
                        }}
                        onClick={() => handleSelectPackage(pkg.id)}
                      >
                        {pkg.imageUrl && (
                          <CardMedia
                            component="img"
                            height="180"
                            image={pkg.imageUrl}
                            alt={pkg.title}
                          />
                        )}
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {pkg.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            ظرفیت: {pkg.capacity} کیلووات
                          </Typography>
                          <Typography variant="h6" color="primary">
                            {pkg.price?.toLocaleString("fa-IR")} ریال
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}

          {orderType === "products" && (
            <>
              <Typography variant="h6" gutterBottom>
                انتخاب محصولات
              </Typography>

              <TextField
                fullWidth
                placeholder="جستجوی محصول..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1 }} />,
                }}
              />

              {productsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {products?.map((product) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                      <Card>
                        <CardMedia
                          component="img"
                          height="160"
                          image={product.imageUrl || "/default-product.jpg"}
                          alt={product.name}
                        />
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom noWrap>
                            {product.name}
                          </Typography>
                          <Typography variant="h6" color="primary">
                            {product.price?.toLocaleString("fa-IR")} ریال
                          </Typography>
                        </CardContent>
                        <CardActions>
                          <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            onClick={() => handleAddToCart(product)}
                            disabled={
                              !product.isAvailable ||
                              cart.items.some(
                                (item) => item.productId === product.id
                              )
                            }
                          >
                            {cart.items.some(
                              (item) => item.productId === product.id
                            )
                              ? "در سبد موجود است"
                              : "افزودن به سبد"}
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}

          {/* Shopping Cart Summary */}
          {cart.items.length > 0 && (
            <Box sx={{ mt: 4, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                سبد خرید ({cart.items.length} مورد)
              </Typography>
              {cart.items.map((item) => (
                <Box
                  key={item.productId}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">{item.productName}</Typography>
                  <Typography variant="body2">
                    {item.quantity} × {item.price?.toLocaleString("fa-IR")}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button onClick={() => navigate("/orders")}>انصراف</Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                orderType === "package"
                  ? !selectedPackageId
                  : cart.items.length === 0
              }
            >
              ادامه
            </Button>
          </Box>
        </Paper>
      )}

      {/* Step 1: Additional Equipment */}
      {activeStep === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            تجهیزات اضافی (اختیاری)
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Alert severity="info" sx={{ mb: 3 }}>
            در این مرحله می‌توانید تجهیزات اضافی مورد نیاز خود را انتخاب کنید
          </Alert>

          <TextField
            fullWidth
            placeholder="جستجوی تجهیزات..."
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1 }} />,
            }}
          />

          {productsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {products?.map((product) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                  <Card>
                    <CardMedia
                      component="img"
                      height="140"
                      image={product.imageUrl || "/default-product.jpg"}
                      alt={product.name}
                    />
                    <CardContent>
                      <Typography variant="body1" gutterBottom noWrap>
                        {product.name}
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {product.price?.toLocaleString("fa-IR")} ریال
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        onClick={() => handleAddToCart(product)}
                        disabled={
                          !product.isAvailable ||
                          cart.items.some(
                            (item) => item.productId === product.id
                          )
                        }
                      >
                        {cart.items.some(
                          (item) => item.productId === product.id
                        )
                          ? "در سبد موجود است"
                          : "افزودن"}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button onClick={handleBack}>بازگشت</Button>
            <Button variant="contained" onClick={handleNext}>
              ادامه
            </Button>
          </Box>
        </Paper>
      )}

      {/* Step 2: Delivery Information */}
      {activeStep === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            اطلاعات تحویل
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="آدرس تحویل"
                multiline
                rows={3}
                value={deliveryInfo.address}
                onChange={(e) =>
                  handleDeliveryInfoChange("address", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>شهر</InputLabel>
                <Select
                  value={deliveryInfo.city}
                  label="شهر"
                  onChange={(e) =>
                    handleDeliveryInfoChange("city", e.target.value)
                  }
                >
                  {iranCities.map((city) => (
                    <MenuItem key={city} value={city}>
                      {city}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="کد پستی"
                value={deliveryInfo.postalCode}
                onChange={(e) =>
                  handleDeliveryInfoChange("postalCode", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="شماره تماس"
                value={deliveryInfo.phoneNumber}
                onChange={(e) =>
                  handleDeliveryInfoChange("phoneNumber", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="تاریخ تحویل مورد نظر"
                value={deliveryInfo.deliveryDate}
                onChange={(e) =>
                  handleDeliveryInfoChange("deliveryDate", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="توضیحات اضافی"
                multiline
                rows={2}
                value={deliveryInfo.notes}
                onChange={(e) =>
                  handleDeliveryInfoChange("notes", e.target.value)
                }
              />
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button onClick={handleBack}>بازگشت</Button>
            <Button variant="contained" onClick={handleNext}>
              ادامه
            </Button>
          </Box>
        </Paper>
      )}

      {/* Step 3: Review and Confirm */}
      {activeStep === 3 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            بررسی نهایی سفارش
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {/* Package Info */}
          {selectedPackage && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ fontWeight: "bold" }}
              >
                پکیج انتخابی
              </Typography>
              <Card>
                <CardContent>
                  <Typography variant="h6">{selectedPackage.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    ظرفیت: {selectedPackage.capacity} کیلووات
                  </Typography>
                  <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                    {selectedPackage.price?.toLocaleString("fa-IR")} ریال
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Cart Items */}
          {cart.items.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ fontWeight: "bold" }}
              >
                محصولات
              </Typography>
              <TableContainer component={Card}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>محصول</TableCell>
                      <TableCell align="center">تعداد</TableCell>
                      <TableCell align="right">قیمت واحد</TableCell>
                      <TableCell align="right">جمع</TableCell>
                      <TableCell align="center">عملیات</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cart.items.map((item) => (
                      <TableRow key={item.productId}>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleUpdateQuantity(
                                  item.productId,
                                  item.quantity - 1
                                )
                              }
                            >
                              <RemoveIcon />
                            </IconButton>
                            <Typography sx={{ mx: 2 }}>
                              {item.quantity}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleUpdateQuantity(
                                  item.productId,
                                  item.quantity + 1
                                )
                              }
                            >
                              <AddIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {item.price?.toLocaleString("fa-IR")} ریال
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {(item.price * item.quantity).toLocaleString("fa-IR")}{" "}
                          ریال
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveFromCart(item.productId)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Delivery Info */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ fontWeight: "bold" }}
            >
              اطلاعات تحویل
            </Typography>
            <Card>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      آدرس:
                    </Typography>
                    <Typography>{deliveryInfo.address}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      شهر:
                    </Typography>
                    <Typography>{deliveryInfo.city}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      شماره تماس:
                    </Typography>
                    <Typography>{deliveryInfo.phoneNumber}</Typography>
                  </Grid>
                  {deliveryInfo.postalCode && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        کد پستی:
                      </Typography>
                      <Typography>{deliveryInfo.postalCode}</Typography>
                    </Grid>
                  )}
                  {deliveryInfo.deliveryDate && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        تاریخ تحویل:
                      </Typography>
                      <Typography>{deliveryInfo.deliveryDate}</Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Box>

          {/* Order Total */}
          {orderTotal && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: "primary.light",
                borderRadius: 1,
              }}
            >
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography>جمع کل:</Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: "left" }}>
                  <Typography sx={{ fontWeight: "bold" }}>
                    {orderTotal.subtotal?.toLocaleString("fa-IR")} ریال
                  </Typography>
                </Grid>

                {orderTotal.discount > 0 && (
                  <>
                    <Grid item xs={6}>
                      <Typography color="error">تخفیف:</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: "left" }}>
                      <Typography color="error">
                        -{orderTotal.discount?.toLocaleString("fa-IR")} ریال
                      </Typography>
                    </Grid>
                  </>
                )}

                {orderTotal.tax > 0 && (
                  <>
                    <Grid item xs={6}>
                      <Typography>مالیات:</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: "left" }}>
                      <Typography>
                        {orderTotal.tax?.toLocaleString("fa-IR")} ریال
                      </Typography>
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="h6">مبلغ نهایی:</Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: "left" }}>
                  <Typography
                    variant="h6"
                    color="primary"
                    sx={{ fontWeight: "bold" }}
                  >
                    {orderTotal.totalAmount?.toLocaleString("fa-IR")} ریال
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
              />
            }
            label="شرایط و قوانین را مطالعه کردم و می‌پذیرم"
          />

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button onClick={handleBack} disabled={submitting}>
              بازگشت
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!acceptTerms || submitting}
              startIcon={
                submitting ? <CircularProgress size={20} /> : <CheckIcon />
              }
            >
              {submitting ? "در حال ثبت..." : "ثبت سفارش"}
            </Button>
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default OrderCreate;
